import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  LinearProgress,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  Radio,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  AccessTime,
  LocationOn,
  People,
  Edit,
  Delete,
  PersonAdd,
  PersonRemove,
  CheckCircle,
  Repeat,
  Campaign,
  BookmarkAdd,
} from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';
import { getEventById, updateEvent, deleteEvent, deleteEventSeries, getEventsBySeries } from '@services/eventService';
import {
  createRegistration,
  getExistingRegistration,
  getVolunteerRegistrationsForEvent,
  getRegistrationsByEvent,
  cancelRegistration,
  cancelVolunteerSeriesRegistrations,
} from '@services/registrationService';
import { getVolunteersByOrganization } from '@services/userService';
import { getPreRegisteredVolunteersByOrg } from '@services/preRegisteredVolunteerService';
import { sendEventAnnouncement } from '@services/notificationService';
import { saveTemplate } from '@services/templateService';
import { logActivity, LOG_TYPES } from '@services/activityLogService';
import ResponsiveDialog from '@components/common/ResponsiveDialog';

const EventReviewPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { userData: user } = useAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [userRegistrations, setUserRegistrations] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteScope, setDeleteScope] = useState('single');
  const [registerScope, setRegisterScope] = useState('single');
  const [addVolunteerScope, setAddVolunteerScope] = useState('single');
  const [allRegistrations, setAllRegistrations] = useState([]);
  const [addVolunteerDialogOpen, setAddVolunteerDialogOpen] = useState(false);
  const [volunteers, setVolunteers] = useState([]);
  const [selectedVolunteer, setSelectedVolunteer] = useState('');
  const [selectedAssignmentForAdd, setSelectedAssignmentForAdd] = useState('');
  const [addingVolunteer, setAddingVolunteer] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        setError('');
        const eventData = await getEventById(eventId);
        setEvent(eventData);

        // Fetch user's own registrations for this event (all roles can register)
        if (user?.uid) {
          const registrations = await getVolunteerRegistrationsForEvent(user.uid, eventId);
          setUserRegistrations(registrations);
        }

        // Fetch all registrations for this event (if org admin)
        // Note: We pass organizationId to help with Firestore security rules
        if (user?.userType === 'org-admin' || user?.userType === 'platform-admin') {
          try {
            const allRegs = await getRegistrationsByEvent(eventId, eventData.organizationId);
            // Filter to only show active registrations
            const activeRegs = allRegs.filter(reg => reg.status === 'registered');
            setAllRegistrations(activeRegs);
          } catch (regErr) {
            console.warn('Could not fetch registrations (may require permissions):', regErr);
            // Don't show error to user - just don't display volunteer list
            setAllRegistrations([]);
          }
        }
      } catch (err) {
        console.error('Error fetching event:', err);
        setError('Failed to load event details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEvent();
    }
  }, [eventId, user?.uid, user?.userType]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleRegisterClick = (assignment) => {
    const conflict = getOverlapConflict(assignment);
    if (conflict) {
      setError(
        `Time conflict: "${assignment.name}" (${fmt12(assignment.startTime)}–${fmt12(assignment.endTime)}) overlaps with your "${conflict.name}" registration (${fmt12(conflict.startTime)}–${fmt12(conflict.endTime)}).`
      );
      return;
    }
    setSelectedAssignment(assignment);
    setRegisterScope('single');
    setRegisterDialogOpen(true);
  };

  const handleRegister = async () => {
    try {
      setRegistering(true);
      setError('');

      const now = new Date();

      // Determine which events to register for
      let eventsToRegister;
      if (registerScope === 'series' && event?.recurrenceId) {
        const seriesEvents = await getEventsBySeries(event.recurrenceId, event.organizationId);
        // Only future/current events
        eventsToRegister = seriesEvents.filter(e => e.date?.toDate() >= now);
      } else {
        eventsToRegister = [{ eventId, eventTitle: event.title, date: event.date }];
      }

      let skipped = 0;
      for (const ev of eventsToRegister) {
        const existing = await getExistingRegistration(user.uid, ev.eventId, selectedAssignment.name);
        if (existing) { skipped++; continue; }
        await createRegistration({
          volunteerId: user.uid,
          volunteerName: user.displayName || user.email,
          eventId: ev.eventId,
          eventTitle: ev.eventTitle || ev.title || event.title,
          assignmentName: selectedAssignment.name,
          assignmentStartTime: selectedAssignment.startTime || null,
          assignmentEndTime: selectedAssignment.endTime || null,
          organizationId: event.organizationId,
          eventDate: ev.date,
        });
      }

      if (eventsToRegister.length > 0 && skipped === eventsToRegister.length) {
        setError(`You are already registered for the "${selectedAssignment.name}" assignment in all selected events.`);
        setRegisterDialogOpen(false);
        setSelectedAssignment(null);
        return;
      }

      // Refresh user's registrations for this event
      const updatedRegistrations = await getVolunteerRegistrationsForEvent(user.uid, eventId);
      setUserRegistrations(updatedRegistrations);

      setRegisterDialogOpen(false);
      setSelectedAssignment(null);
      navigate('/schedule');
    } catch (err) {
      console.error('Error registering:', err);
      setError('Failed to register for assignment. Please try again.');
      setRegisterDialogOpen(false);
      setSelectedAssignment(null);
    } finally {
      setRegistering(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteScope('single');
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      setError('');

      if (deleteScope === 'series' && event?.recurrenceId) {
        await deleteEventSeries(event.recurrenceId, event.organizationId);
      } else {
        await deleteEvent(eventId);
      }

      logActivity({
        organizationId: user.organizationId,
        organizationName: user.organizationName,
        type: LOG_TYPES.EVENT_DELETED,
        actorId: user.uid,
        actorName: user.displayName,
        targetId: eventId,
        targetName: event?.title,
      });

      navigate('/dashboard');
    } catch (err) {
      console.error('Error deleting event:', err);
      setError('Failed to delete event. Please try again.');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  // Handle add volunteer dialog
  const handleAddVolunteerClick = async () => {
    try {
      setError('');
      // Fetch volunteers from the organization, plus anyone pre-registered by an
      // admin who hasn't signed up yet (email-only reminders until they claim an account)
      const [orgVolunteers, preRegVolunteers] = await Promise.all([
        getVolunteersByOrganization(event.organizationId),
        getPreRegisteredVolunteersByOrg(event.organizationId),
      ]);
      // Filter out volunteers who are already registered for this event
      const registeredVolunteerIds = allRegistrations.map(reg => reg.volunteerId);
      const availableVolunteers = orgVolunteers.filter(
        vol => !registeredVolunteerIds.includes(vol.uid) && vol.status === 'active'
      );
      const availablePreReg = preRegVolunteers
        .filter(vol => !registeredVolunteerIds.includes(vol.id))
        .map(vol => ({ uid: vol.id, displayName: vol.displayName, email: vol.email, status: 'active', isPending: true }));
      setVolunteers([...availableVolunteers, ...availablePreReg]);
      setSelectedVolunteer('');
      setSelectedAssignmentForAdd('');
      setAddVolunteerScope('single');
      setAddVolunteerDialogOpen(true);
    } catch (err) {
      console.error('Error fetching volunteers:', err);
      setError('Failed to load volunteers. Please try again.');
    }
  };

  const handleConfirmAddVolunteer = async () => {
    try {
      setAddingVolunteer(true);
      setError('');

      if (!selectedVolunteer || !selectedAssignmentForAdd) {
        setError('Please select both a volunteer and an assignment.');
        return;
      }

      const volunteer = volunteers.find(v => v.uid === selectedVolunteer);
      if (!volunteer) {
        setError('Selected volunteer not found.');
        return;
      }

      const now = new Date();

      // Determine which events to register for
      let eventsToRegister;
      if (addVolunteerScope === 'series' && event?.recurrenceId) {
        const seriesEvents = await getEventsBySeries(event.recurrenceId, event.organizationId);
        eventsToRegister = seriesEvents.filter(e => e.date?.toDate() >= now);
      } else {
        eventsToRegister = [{ eventId, eventTitle: event.title, date: event.date }];
      }

      const assignmentDef = event.assignments?.find(a => a.name === selectedAssignmentForAdd);
      for (const ev of eventsToRegister) {
        const existing = await getExistingRegistration(volunteer.uid, ev.eventId, selectedAssignmentForAdd, event.organizationId);
        if (existing) continue;
        await createRegistration({
          volunteerId: volunteer.uid,
          volunteerName: volunteer.displayName || volunteer.email,
          volunteerType: volunteer.isPending ? 'pending' : 'user',
          eventId: ev.eventId,
          eventTitle: ev.eventTitle || ev.title || event.title,
          assignmentName: selectedAssignmentForAdd,
          assignmentStartTime: assignmentDef?.startTime || null,
          assignmentEndTime: assignmentDef?.endTime || null,
          organizationId: event.organizationId,
          eventDate: ev.date,
        });
      }

      // Refresh registrations for the current event
      const allRegs = await getRegistrationsByEvent(eventId, event.organizationId);
      const activeRegs = allRegs.filter(reg => reg.status === 'registered');
      setAllRegistrations(activeRegs);

      setAddVolunteerDialogOpen(false);
      setSelectedVolunteer('');
      setSelectedAssignmentForAdd('');
    } catch (err) {
      console.error('Error adding volunteer:', err);
      setError('Failed to add volunteer. Please try again.');
    } finally {
      setAddingVolunteer(false);
    }
  };

  const [removeTarget, setRemoveTarget] = useState(null); // { registrationId, volunteerId, volunteerName, assignmentName }
  const [removeScope, setRemoveScope] = useState('single');
  const [removing, setRemoving] = useState(false);

  const [selfCancelTarget, setSelfCancelTarget] = useState(null); // { registrationId, assignmentName }
  const [selfCancelScope, setSelfCancelScope] = useState('single');
  const [selfCancelReason, setSelfCancelReason] = useState('');
  const [selfCancelling, setSelfCancelling] = useState(false);
  const [announcing, setAnnouncing] = useState(false);
  const [announceSnackbar, setAnnounceSnackbar] = useState('');
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const handleConfirmRemoveVolunteer = async () => {
    try {
      setRemoving(true);
      if (removeScope === 'series' && event?.recurrenceId) {
        const now = new Date();
        const seriesEvents = await getEventsBySeries(event.recurrenceId, event.organizationId);
        const futureEventIds = seriesEvents
          .filter(e => e.date?.toDate() >= now)
          .map(e => e.eventId);
        await cancelVolunteerSeriesRegistrations(
          removeTarget.volunteerId,
          futureEventIds,
          removeTarget.assignmentName
        );
      } else {
        await cancelRegistration(removeTarget.registrationId);
      }
      const allRegs = await getRegistrationsByEvent(eventId, event.organizationId);
      setAllRegistrations(allRegs.filter(r => r.status === 'registered'));
      setRemoveTarget(null);
    } catch (err) {
      console.error('Error removing volunteer:', err);
      setError('Failed to remove volunteer. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  const handleSelfCancelConfirm = async () => {
    if (!selfCancelTarget) return;
    try {
      setSelfCancelling(true);
      if (selfCancelScope === 'series' && event?.recurrenceId) {
        const now = new Date();
        const seriesEvents = await getEventsBySeries(event.recurrenceId, event.organizationId);
        const futureEventIds = seriesEvents
          .filter(e => e.date?.toDate() >= now)
          .map(e => e.eventId);
        await cancelVolunteerSeriesRegistrations(user.uid, futureEventIds, selfCancelTarget.assignmentName, selfCancelReason);
      } else {
        await cancelRegistration(selfCancelTarget.registrationId, selfCancelReason);
      }
      const regs = await getVolunteerRegistrationsForEvent(user.uid, eventId);
      setUserRegistrations(regs.filter(r => r.status === 'registered'));
      setSelfCancelTarget(null);
      setSelfCancelReason('');
    } catch (err) {
      console.error('Error cancelling registration:', err);
      setError('Failed to cancel registration. Please try again.');
    } finally {
      setSelfCancelling(false);
    }
  };

  const handleAnnounceEvent = async () => {
    try {
      setAnnouncing(true);
      const { sent, skipped, allFull } = await sendEventAnnouncement(eventId, 'open_spots');
      if (allFull) {
        setAnnounceSnackbar('All positions are full — no notification sent.');
      } else {
        const skipNote = skipped > 0 ? ` (${skipped} already signed up, skipped)` : '';
        setAnnounceSnackbar(`Open spots notification sent to ${sent} volunteer${sent !== 1 ? 's' : ''}${skipNote}.`);
      }
    } catch (err) {
      console.error('Error sending announcement:', err);
      const msg = err?.message || err?.details || 'Failed to send notification. Please try again.';
      setAnnounceSnackbar(`Error: ${msg}`);
    } finally {
      setAnnouncing(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim() || !event) return;
    try {
      setSavingTemplate(true);
      await saveTemplate(user.organizationId, templateName.trim(), {
        title: event.title,
        description: event.description,
        location: event.location,
        startTime: event.startTime,
        endTime: event.endTime,
        volunteersNeeded: event.volunteersNeeded,
        assignments: (event.assignments || []).map(({ name, description, volunteersNeeded, startTime, endTime }) => ({
          name, description: description || '', volunteersNeeded, startTime: startTime || '', endTime: endTime || '',
        })),
      }, user.uid);
      setSaveTemplateOpen(false);
      setTemplateName('');
      setAnnounceSnackbar(`Template "${templateName.trim()}" saved.`);
    } catch (err) {
      console.error('Error saving template:', err);
    } finally {
      setSavingTemplate(false);
    }
  };

  const getRecurrenceLabel = (ev) => {
    if (!ev?.isRecurring || !ev?.recurrencePattern) return null;
    switch (ev.recurrencePattern) {
      case 'weekly':    return 'Repeats every week';
      case 'biweekly':  return 'Repeats every 2 weeks';
      case 'monthly':   return 'Repeats monthly';
      case 'monthly-weekday': {
        const week = ev.monthlyWeekNumber
          ? ev.monthlyWeekNumber.charAt(0).toUpperCase() + ev.monthlyWeekNumber.slice(1)
          : '';
        const day = ev.monthlyDayOfWeek
          ? ev.monthlyDayOfWeek.charAt(0).toUpperCase() + ev.monthlyDayOfWeek.slice(1)
          : '';
        return `Repeats every ${week} ${day} of the month`;
      }
      default: return 'Recurring event';
    }
  };

  const isOrgAdmin = user?.userType === 'org-admin' || user?.userType === 'platform-admin';
  const canEdit = isOrgAdmin && user?.organizationId === event?.organizationId;

  // Check if user is registered for a specific assignment
  const isRegisteredForAssignment = (assignmentName) => {
    return userRegistrations.some((reg) => reg.assignmentName === assignmentName);
  };

  const getRegistrationForAssignment = (assignmentName) => {
    return userRegistrations.find((reg) => reg.assignmentName === assignmentName);
  };

  // Format HH:MM to 12-hour display
  const fmt12 = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${((h % 12) || 12)}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  // Check if a new assignment overlaps with any existing registration (current event only)
  const getOverlapConflict = (assignment) => {
    if (!assignment.startTime || !assignment.endTime) return null;
    for (const reg of userRegistrations) {
      const existing = event.assignments?.find(a => a.name === reg.assignmentName);
      if (!existing?.startTime || !existing?.endTime) continue;
      if (assignment.startTime < existing.endTime && assignment.endTime > existing.startTime) {
        return existing;
      }
    }
    return null;
  };

  // Group registrations by assignment for org admins
  const getVolunteersByAssignment = () => {
    const grouped = {};

    allRegistrations.forEach((reg) => {
      if (!grouped[reg.assignmentName]) {
        grouped[reg.assignmentName] = [];
      }
      grouped[reg.assignmentName].push(reg);
    });

    return grouped;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !event) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          variant="outlined"
          sx={{ minHeight: 48 }}
        >
          Back to Events
        </Button>

        {canEdit && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
            <Button
              startIcon={announcing ? <CircularProgress size={18} color="inherit" /> : <Campaign />}
              variant="outlined"
              color="primary"
              onClick={handleAnnounceEvent}
              disabled={announcing}
              sx={{ minHeight: 48 }}
            >
              {announcing ? 'Sending...' : 'Notify Volunteers'}
            </Button>
            <Button
              startIcon={<BookmarkAdd />}
              variant="outlined"
              onClick={() => { setTemplateName(event?.title || ''); setSaveTemplateOpen(true); }}
              sx={{ minHeight: 48 }}
            >
              Save as Template
            </Button>
            <Button
              startIcon={<Edit />}
              variant="outlined"
              onClick={() => navigate(`/events/${eventId}/edit`)}
              sx={{ minHeight: 48 }}
            >
              Edit Event
            </Button>
            <Button
              startIcon={<Delete />}
              variant="outlined"
              color="error"
              onClick={handleDeleteClick}
              sx={{ minHeight: 48 }}
            >
              Delete
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Event Details Card */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="h4" component="h1" fontWeight="bold">
              {event?.title}
            </Typography>
            {event?.isRecurring && (
              <Chip
                label={`Event ${event.instanceNumber} of ${event.totalInstances}`}
                color="primary"
                size="small"
              />
            )}
          </Box>
          {event?.isRecurring && getRecurrenceLabel(event) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Repeat fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {getRecurrenceLabel(event)} &bull; {event.totalInstances} total events
              </Typography>
            </Box>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {event?.organizationName}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Event Info Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CalendarToday color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Date
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(event?.date)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <AccessTime color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Time
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatTime(event?.startTime)} - {formatTime(event?.endTime)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <LocationOn color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Location
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {event?.location}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <People color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Volunteers Needed
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {event?.volunteersNeeded} volunteers
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Description */}
        <Box>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Description
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {event?.description}
          </Typography>
        </Box>
      </Paper>

      {/* Assignments Section */}
      {event?.assignments && event.assignments.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            Volunteer Assignments
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose an assignment to sign up for this event
          </Typography>

          <Grid container spacing={3}>
            {[...event.assignments].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((assignment, index) => {
              const liveCount = isOrgAdmin
                ? (getVolunteersByAssignment()[assignment.name]?.length || 0)
                : (assignment.volunteersAssigned || 0);
              const spotsLeft = assignment.volunteersNeeded - liveCount;
              const percentFilled = (liveCount / assignment.volunteersNeeded) * 100;
              const isFull = spotsLeft <= 0;

              return (
                <Grid item xs={12} md={6} key={index}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      borderColor: isFull ? 'grey.300' : 'primary.main',
                      bgcolor: isFull ? 'grey.50' : 'background.paper',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="h6" fontWeight="bold">
                            {assignment.name}
                          </Typography>
                          {isFull ? (
                            <Chip label="Full" color="default" size="small" />
                          ) : (
                            <Chip
                              label={`${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
                              color="success"
                              size="small"
                            />
                          )}
                        </Box>
                        {assignment.startTime && assignment.endTime && (
                          <Typography variant="body2" color="primary" fontWeight="medium" sx={{ mb: 0.5 }}>
                            {fmt12(assignment.startTime)} – {fmt12(assignment.endTime)}
                          </Typography>
                        )}
                        {assignment.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {assignment.description}
                          </Typography>
                        )}
                      </Box>

                      {/* Progress Bar */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Volunteers
                          </Typography>
                          <Typography variant="caption" fontWeight="medium">
                            {liveCount} / {assignment.volunteersNeeded}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(percentFilled, 100)}
                          sx={{ height: 8, borderRadius: 1 }}
                        />
                      </Box>

                      {/* Sign Up Button or Registered Badge */}
                      <>
                        {isRegisteredForAssignment(assignment.name) ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 1,
                                  p: 1.5,
                                  bgcolor: 'success.lighter',
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: 'success.main',
                                }}
                              >
                                <CheckCircle color="success" />
                                <Typography variant="body2" fontWeight="bold" color="success.main">
                                  You're Registered
                                </Typography>
                              </Box>
                              <Button
                                fullWidth
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<PersonRemove />}
                                onClick={() => {
                                  const reg = getRegistrationForAssignment(assignment.name);
                                  setSelfCancelScope('single');
                                  setSelfCancelReason('');
                                  setSelfCancelTarget({ registrationId: reg.registrationId, assignmentName: assignment.name });
                                }}
                              >
                                Unregister
                              </Button>
                            </Box>
                          ) : (
                            <Button
                              fullWidth
                              variant={isFull ? 'outlined' : 'contained'}
                              disabled={isFull}
                              startIcon={isFull ? <CheckCircle /> : <PersonAdd />}
                              onClick={() => handleRegisterClick(assignment)}
                            >
                              {isFull ? 'Fully Booked' : 'Sign Up for This Role'}
                            </Button>
                          )}
                      </>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Registered Volunteers Section (Org Admin Only) */}
      {isOrgAdmin && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Registered Volunteers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Volunteers who have signed up for this event
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<PersonAdd />}
              onClick={handleAddVolunteerClick}
              sx={{ minHeight: 48 }}
            >
              Add Volunteer
            </Button>
          </Box>

          {allRegistrations.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No volunteers registered yet. Click "Add Volunteer" to manually register volunteers.
            </Alert>
          ) : null}

          <Grid container spacing={3}>
            {[...(event?.assignments || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((assignment, index) => {
              const volunteersByAssignment = getVolunteersByAssignment();
              const volunteers = volunteersByAssignment[assignment.name] || [];

              return (
                <Grid item xs={12} md={6} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: assignment.startTime && assignment.endTime ? 0.5 : 2 }}>
                        {assignment.name}
                      </Typography>
                      {assignment.startTime && assignment.endTime && (
                        <Typography variant="body2" color="primary" fontWeight="medium" sx={{ mb: 2 }}>
                          {fmt12(assignment.startTime)} – {fmt12(assignment.endTime)}
                        </Typography>
                      )}

                      {volunteers.length === 0 ? (
                        <Box
                          sx={{
                            p: 3,
                            textAlign: 'center',
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No volunteers registered yet
                          </Typography>
                        </Box>
                      ) : (
                        <List dense>
                          {volunteers.map((reg, idx) => (
                            <ListItem
                              key={idx}
                              sx={{
                                px: 0,
                                borderBottom: idx < volunteers.length - 1 ? 1 : 0,
                                borderColor: 'divider',
                                pr: 6,
                              }}
                            >
                              <ListItemText
                                primary={reg.volunteerName}
                                secondary={`Registered ${reg.registeredAt?.toDate().toLocaleDateString()}`}
                                primaryTypographyProps={{ fontWeight: 'medium' }}
                              />
                              <ListItemSecondaryAction>
                                <Tooltip title="Remove volunteer">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => { setRemoveScope('single'); setRemoveTarget({ registrationId: reg.registrationId, volunteerId: reg.volunteerId, volunteerName: reg.volunteerName, assignmentName: reg.assignmentName }); }}
                                  >
                                    <PersonRemove fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      )}

                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                          {volunteers.length} of {assignment.volunteersNeeded} spots filled
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Registration Confirmation Dialog */}
      <ResponsiveDialog
        open={registerDialogOpen}
        onClose={() => setRegisterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Registration</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You are signing up for:
          </Typography>
          <Box sx={{ my: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {selectedAssignment?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {event?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(event?.date)} at {formatTime(event?.startTime)}
            </Typography>
          </Box>

          {event?.isRecurring && event?.recurrenceId && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Repeat fontSize="small" color="primary" />
                <Typography variant="body2" color="primary.main" fontWeight="medium">
                  {getRecurrenceLabel(event)}
                  {event.recurrenceEndDate && (() => {
                    const d = event.recurrenceEndDate?.toDate?.() ?? new Date(event.recurrenceEndDate);
                    return <> &bull; ends {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>;
                  })()}
                </Typography>
              </Box>
              <Typography variant="body2" gutterBottom>
                Would you like to sign up for just this event or all upcoming events in the series?
              </Typography>
              <RadioGroup value={registerScope} onChange={(e) => setRegisterScope(e.target.value)}>
                <FormControlLabel value="single" control={<Radio size="small" />} label="This event only" />
                <FormControlLabel
                  value="series"
                  control={<Radio size="small" />}
                  label={(() => {
                    const remaining = event.totalInstances && event.instanceNumber
                      ? event.totalInstances - event.instanceNumber + 1
                      : event.totalInstances;
                    return `All upcoming events in this series (${remaining} remaining)`;
                  })()}
                />
              </RadioGroup>
            </Box>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            By registering, you commit to attending. Please only sign up if you're sure you can make it.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterDialogOpen(false)} disabled={registering}>
            Cancel
          </Button>
          <Button
            onClick={handleRegister}
            variant="contained"
            disabled={registering}
            startIcon={registering ? <CircularProgress size={20} /> : <PersonAdd />}
          >
            {registering ? 'Registering...' : 'Confirm Registration'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Delete Confirmation Dialog */}
      <ResponsiveDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Event?</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2, p: 2, bgcolor: 'error.lighter', borderRadius: 1, border: 1, borderColor: 'error.main' }}>
            <Typography variant="h6" fontWeight="bold">
              {event?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(event?.date)} at {formatTime(event?.startTime)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {event?.volunteersRegistered || 0} volunteer(s) registered
            </Typography>
          </Box>

          {event?.isRecurring && event?.recurrenceId && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                This is a recurring event (instance {event.instanceNumber} of {event.totalInstances}). What would you like to delete?
              </Typography>
              <RadioGroup
                value={deleteScope}
                onChange={(e) => setDeleteScope(e.target.value)}
              >
                <FormControlLabel
                  value="single"
                  control={<Radio />}
                  label="This event only"
                />
                <FormControlLabel
                  value="series"
                  control={<Radio />}
                  label={`All ${event.totalInstances} events in this series`}
                />
              </RadioGroup>
            </Box>
          )}

          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. All registrations for the deleted event(s) will also be removed.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : deleteScope === 'series' ? 'Delete All in Series' : 'Delete Event'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Add Volunteer Dialog */}
      <ResponsiveDialog
        open={addVolunteerDialogOpen}
        onClose={() => !addingVolunteer && setAddVolunteerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Volunteer to Event</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a volunteer and assignment to manually register them for this event
          </Typography>

          {volunteers.length === 0 ? (
            <Alert severity="info">
              No available volunteers found. All active volunteers may already be registered for this event.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Select Volunteer</InputLabel>
                <Select
                  value={selectedVolunteer}
                  onChange={(e) => setSelectedVolunteer(e.target.value)}
                  label="Select Volunteer"
                  disabled={addingVolunteer}
                >
                  {volunteers.map((volunteer) => (
                    <MenuItem key={volunteer.uid} value={volunteer.uid}>
                      {volunteer.displayName || volunteer.email}
                      {volunteer.email && volunteer.displayName && ` (${volunteer.email})`}
                      {volunteer.isPending && ' — pending signup, email reminders only'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Select Assignment</InputLabel>
                <Select
                  value={selectedAssignmentForAdd}
                  onChange={(e) => setSelectedAssignmentForAdd(e.target.value)}
                  label="Select Assignment"
                  disabled={addingVolunteer}
                >
                  {[...(event?.assignments || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((assignment, index) => {
                    const spotsLeft = assignment.volunteersNeeded - (assignment.volunteersAssigned || 0);
                    const isFull = spotsLeft <= 0;
                    return (
                      <MenuItem key={index} value={assignment.name} disabled={isFull}>
                        {assignment.name}
                        {isFull ? ' (Full)' : ` (${spotsLeft} spots left)`}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {event?.isRecurring && event?.recurrenceId && (
                <Box>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Register for how many events?
                  </Typography>
                  <RadioGroup value={addVolunteerScope} onChange={(e) => setAddVolunteerScope(e.target.value)}>
                    <FormControlLabel value="single" control={<Radio />} label="This event only" />
                    <FormControlLabel
                      value="series"
                      control={<Radio />}
                      label={`All upcoming events in this series (${event.totalInstances} total)`}
                    />
                  </RadioGroup>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddVolunteerDialogOpen(false)} disabled={addingVolunteer}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAddVolunteer}
            variant="contained"
            disabled={addingVolunteer || !selectedVolunteer || !selectedAssignmentForAdd || volunteers.length === 0}
            startIcon={addingVolunteer ? <CircularProgress size={20} /> : <PersonAdd />}
          >
            {addingVolunteer ? 'Adding...' : 'Add Volunteer'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
      {/* Remove Volunteer Confirmation Dialog */}
      <ResponsiveDialog
        open={!!removeTarget}
        onClose={() => !removing && setRemoveTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Volunteer?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: event?.isRecurring ? 2 : 0 }}>
            Remove <strong>{removeTarget?.volunteerName}</strong> from the <strong>{removeTarget?.assignmentName}</strong> assignment?
          </Typography>
          {event?.isRecurring && (
            <RadioGroup value={removeScope} onChange={(e) => setRemoveScope(e.target.value)}>
              <FormControlLabel value="single" control={<Radio />} label="This event only" />
              <FormControlLabel value="series" control={<Radio />} label="This and all future events in the series" />
            </RadioGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)} disabled={removing}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemoveVolunteer}
            variant="contained"
            color="error"
            disabled={removing}
            startIcon={removing ? <CircularProgress size={20} /> : <PersonRemove />}
          >
            {removing ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Self-Cancel (Unregister) Confirmation Dialog */}
      <ResponsiveDialog
        open={!!selfCancelTarget}
        onClose={() => !selfCancelling && (setSelfCancelTarget(null), setSelfCancelReason(''))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unregister from Assignment?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Remove yourself from <strong>{selfCancelTarget?.assignmentName}</strong>?
          </Typography>
          {event?.isRecurring && (
            <RadioGroup value={selfCancelScope} onChange={(e) => setSelfCancelScope(e.target.value)} sx={{ mb: 2 }}>
              <FormControlLabel value="single" control={<Radio />} label="This event only" />
              <FormControlLabel value="series" control={<Radio />} label="This and all future events in the series" />
            </RadioGroup>
          )}
          <TextField
            fullWidth
            label="Reason (optional)"
            placeholder="Let the admin know why you're cancelling"
            value={selfCancelReason}
            onChange={(e) => setSelfCancelReason(e.target.value)}
            multiline
            rows={2}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSelfCancelTarget(null); setSelfCancelReason(''); }} disabled={selfCancelling}>
            Keep Registration
          </Button>
          <Button
            onClick={handleSelfCancelConfirm}
            variant="contained"
            color="error"
            disabled={selfCancelling}
            startIcon={selfCancelling ? <CircularProgress size={20} /> : <PersonRemove />}
          >
            {selfCancelling ? 'Cancelling...' : 'Unregister'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <Snackbar
        open={!!announceSnackbar}
        onClose={() => setAnnounceSnackbar('')}
        message={announceSnackbar}
        autoHideDuration={5000}
      />

      {/* Save as Template Dialog */}
      <Dialog open={saveTemplateOpen} onClose={() => setSaveTemplateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save as Template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Saves this event's title, description, location, times, and assignments as a reusable template.
          </Typography>
          <TextField
            label="Template name"
            fullWidth
            autoFocus
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveAsTemplate()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveTemplateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveAsTemplate}
            disabled={savingTemplate || !templateName.trim()}
            startIcon={savingTemplate ? <CircularProgress size={16} /> : <BookmarkAdd />}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventReviewPage;
